import ReturnIcon from '@assets/icons/return-arrow.svg?react'
import TrashIcon from '@assets/icons/trash.svg?react'
import { PriceCard } from '@components/_ui/PriceCard'
import { TooltipInfo } from '@components/_ui/TooltipInfo'
import { quiz } from '@constants/data'
import { usePrice } from '@hooks/usePrice'
import { usePriceSetter } from '@hooks/usePriceSetter'
import { formatPrice } from '@lib/formatPrice'
import { getCleaningType } from '@lib/getCleaningType'
import type { CoreData } from '@types'
import clsx from 'clsx'
import { useState } from 'react'
import { useWatch } from 'react-hook-form'

import s from './PriceDetail.module.scss'
import type { AdditionalItemProps, PriceDetailProps } from './PriceDetail.props'

export function PriceDetail({ sticky, withActions }: PriceDetailProps) {
  const priceData = usePrice()

  const сleaningType = useWatch({ name: 'requested_cleaning_type' })
  const lastTimeCleaned = useWatch({ name: 'last_time_cleaned' })
  const homeState = useWatch({ name: 'home_state' })
  const bed = useWatch({ name: 'bed' })
  const bath = useWatch({ name: 'bath' })
  const bed_basement = useWatch({ name: 'bed_basement' })
  const bath_basement = useWatch({ name: 'bath_basement' })

  const basicDeep = getCleaningType({ homeState, lastTimeCleaned, сleaningType })

  return (
    <div className={clsx(s.wrapper, { [s.stickyWrapper]: sticky })}>
      {(
        !!priceData?.core_service_one_time_price
        || !!priceData?.core_service_basement_one_time_price
      ) && <>
        <h4 className={s.title}>Recommended Main Service</h4>

        {!!priceData?.core_service_one_time_price && (
          <>
            <div className={s.item}>
              <div className={s.itemTitle}>{basicDeep?.replaceAll('_', ' ') + ' Cleaning'}</div>
              <div className={s.itemPrice}>{formatPrice(priceData?.core_service_one_time_price)}</div>
            </div>
            <div className={s.item}>
              <div className={s.itemDescr}>
                {quiz?.data?.core_data?.recommended_main_service?.main?.replaceAll('[bed]', bed)?.replaceAll('[bath]', bath)}
              </div>
            </div>
          </>
        )}

        {!!priceData?.core_service_basement_one_time_price && (
          <>
            <div className={s.item}>
              <div className={s.itemTitle}>{basicDeep?.replaceAll('_', ' ') + ' Basement Cleaning'}</div>
              <div className={s.itemPrice}>{formatPrice(priceData?.core_service_basement_one_time_price)}</div>
            </div>
            <div className={s.item}>
              <div className={s.itemDescr}>
                {quiz?.data?.core_data?.recommended_main_service?.basement?.replaceAll('[bed_basement]', bed_basement)?.replaceAll('[bath_basement]', bath_basement)}
              </div>
            </div>
          </>
        )}

        <div className={s.hr} />
      </>}

      {(
        !!priceData?.additional_options
        && !!Object.values(priceData.additional_options)?.filter(Boolean)?.length
      ) && <>
        <h4 className={s.title}>Additional Services</h4>

        {Object.entries(priceData.additional_options)
          .filter(([_, value]) => value.total)
          .map(([key, value]) => {
            const keyObj = quiz.data?.core_data?.additional_options?.[key as keyof CoreData['additional_options']]

            return (
              <AdditionalItem
                key={key}
                data={{ key, ...keyObj }}
                value={value}
                withActions={withActions}
              />
            )
          }
          )}

        <div className={s.hr} />
      </>}

      {(!!priceData?.sales?.online_booking_discount_amount
        || (!!priceData?.sales?.tidy_home_discount_amount && ['basic', 'airbnb'].includes(basicDeep))
      ) && <>
        <h4 className={s.title}>Applied Savings</h4>

        {!!priceData?.sales?.online_booking_discount_amount && (
          <div className={s.item}>
            <span className={s.itemTitle}>{quiz.data.core_data.online_booking_discount_amount}</span>
            <span className={s.itemPrice}>{formatPrice(priceData?.sales.online_booking_discount_amount)}</span>
          </div>
        )}

        {(!!priceData?.sales?.tidy_home_discount_amount && ['basic', 'airbnb'].includes(basicDeep)) && (
          <div className={s.item}>
            <span className={s.itemTitle}>{quiz.data.core_data.tidy_home_discount_amount}</span>
            <span className={s.itemPrice}>{formatPrice(priceData?.sales.tidy_home_discount_amount)}</span>
          </div>
        )}

        <div className={clsx(s.hr, { [s.hidden]: sticky })} />
      </>}

      <div className={clsx({ [s.sticky]: sticky })}>
        <PriceCard
          total={formatPrice(priceData?.sales?.total_one_time_price)}
          price1={formatPrice(priceData?.subscriptions?.bi_weekly_price ?? quiz?.data?.totals?.subscriptions?.bi_weekly_price)}
          price2={formatPrice(priceData?.subscriptions?.monthly_price ?? quiz?.data?.totals?.subscriptions?.monthly_price)}
        />
      </div>
    </div>
  )
}

function AdditionalItem({ data, value, withActions }: AdditionalItemProps) {
  const setPriceData = usePriceSetter()

  const [removed, setRemoved] = useState(false)

  const removeHandler = () => {
    setPriceData((prev) => ({
      ...prev,
      ...(!!prev?.sales && {
        sales: {
          ...prev.sales,
          total_one_time_price: prev.sales.total_one_time_price - value.total
        }
      }),
      removed_items: prev?.removed_items ? [...prev.removed_items, data.key] : [data.key]
    }))
    setRemoved(true)
  }

  const returnHandler = () => {
    setPriceData((prev) => ({
      ...prev,
      ...(!!prev?.sales && {
        sales: {
          ...prev.sales,
          total_one_time_price: prev.sales.total_one_time_price + value.total
        }
      }),
      removed_items: prev?.removed_items?.filter((i) => i !== data.key)
    }))
    setRemoved(false)
  }

  return (
    <div className={clsx(s.item, { [s.removed]: removed })}>
      <div className={s.itemTitle}>
        <div className={s.itemWrapper}>
          {data?.title}
          {!!data?.additional_info && (
            <TooltipInfo text={data.additional_info} />
          )}
        </div>
        {value?.qty && (
          <span className={s.itemDescr}>{data?.description}{' '}{value.qty}</span>
        )}
      </div>

      <div className={clsx(s.itemActionWrapper, { [s.margin]: value?.qty })}>
        <div className={s.itemPrice}>{formatPrice(value.total)}</div>

        {withActions && (
          <div
            className={s.itemAction}
            onClick={() => removed ? returnHandler() : removeHandler()}
          >
            {removed ? (
              <ReturnIcon className={s.itemActionIcon} />
            ) : (
              <TrashIcon />
            )}
          </div>
        )}
      </div>
    </div>
  )
}
